import { create } from 'zustand';

import { tokenStorage } from '@/lib/auth/tokenStorage';
import { useTenantStore } from '@/stores/tenantStore';

import type { AccessTokenResult, AuthResult } from '../schemas/authSchemas';
import { type SessionUser, sessionUserFromAccessToken } from '../utils/accessTokenSession';

interface AuthState {
  user: SessionUser | null;
  isAuthenticated: boolean;

  // Called after a successful /signup, /login, or /refresh response. Persists
  // tokens to sessionStorage (access) + localStorage (refresh), mirrors the
  // tenant id into the cross-cutting tenantStore so the X-Tenant-Id header
  // interceptor finds it, and stashes the user for UI consumption.
  setSession: (result: AuthResult) => void;
  setAccessTokenSession: (result: AccessTokenResult) => void;

  // Clears the session: in-memory user + storage tokens + tenantStore.
  // Routing back to /login is the caller's responsibility (ProtectedRoute
  // handles the redirect when an unauthenticated user hits a guarded route).
  clearSession: () => void;

  // Flips the local cached UserDto.onboardingCompleted to true after the
  // /api/v1/me/complete-onboarding mutation succeeds. Tokens stay the same
  // -- we don't re-issue them on this flag change.
  markOnboardingComplete: () => void;

  // Flips the local cached UserDto.isEmailVerified to true after a
  // successful /api/v1/auth/verify-email call. Same shape as
  // markOnboardingComplete -- tokens unchanged, just the flag.
  markEmailVerified: () => void;

  // Pins a freshly-created tenantId onto the cached UserDto and mirrors it
  // into tenantStore so the X-Tenant-Id header starts firing. Called after
  // POST /api/v1/tenants succeeds during the Organization onboarding branch
  // -- the JWT itself doesn't carry tenantId (it's resolved per-request from
  // memberships), so no token rotation is needed.
  attachTenant: (tenantId: string) => void;
}

clearLegacySessionStorage();

const initialUser = sessionUserFromAccessToken(tokenStorage.getAccessToken());

// Expired or malformed token — clear storage now so the store starts clean
// and ProtectedRoute triggers the OAuth redirect immediately.
if (!initialUser) {
  tokenStorage.clearAll();
}

export const useAuthStore = create<AuthState>((set) => ({
  user: initialUser,
  isAuthenticated: Boolean(initialUser),

  setSession: (result) => {
    clearLegacySessionStorage();
    tokenStorage.setAccessToken(result.accessToken);
    tokenStorage.setRefreshToken(result.refreshToken);
    const user = sessionUserFromAccessToken(result.accessToken);
    useTenantStore.getState().setTenantId(user?.tenantId ?? null);
    set({ user, isAuthenticated: Boolean(user) });
  },

  setAccessTokenSession: (result) => {
    clearLegacySessionStorage();
    tokenStorage.setAccessToken(result.accessToken);
    const user = sessionUserFromAccessToken(result.accessToken);
    useTenantStore.getState().setTenantId(user?.tenantId ?? null);
    set({ user, isAuthenticated: Boolean(user) });
  },

  clearSession: () => {
    tokenStorage.clearAll();
    clearLegacySessionStorage();
    useTenantStore.getState().clear();
    set({ user: null, isAuthenticated: false });
  },

  markOnboardingComplete: () =>
    set((s) => (s.user ? { user: { ...s.user, onboardingCompleted: true } } : s)),

  markEmailVerified: () =>
    set((s) => (s.user ? { user: { ...s.user, isEmailVerified: true } } : s)),

  attachTenant: (tenantId) => {
    useTenantStore.getState().setTenantId(tenantId);
    set((s) => (s.user ? { user: { ...s.user, tenantId } } : s));
  },
}));

useTenantStore.getState().setTenantId(initialUser?.tenantId ?? null);

function clearLegacySessionStorage(): void {
  localStorage.removeItem('signcare.auth');
  localStorage.removeItem('signcare.tenant');
}
